import React, {useEffect, useState} from "react";
import {Button, Col, Descriptions, Popover, Row, Table, Tooltip} from "antd";
import {ReloadOutlined, PlusOutlined, EditOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {useStore} from "../../../../hooks/useStore";
import {MealCardIntegrationCreateModal} from "../modals/mealCardIntegrationCreateModal/MealCardIntegrationCreateModal";
import {MealCardIntegrationUpdateModal} from "../modals/mealCardIntegrationUpdateModal/MealCardIntegrationUpdateModal";
import JSONInput from "react-json-editor-ajrm";
import {HideableText} from "../../../_defaultComponents/HideableText/HideableText";

import {useTranslation} from "react-i18next";

export const SubcompanyMealCardIntegrationList = ({subcompanyId, ...props}) => {
    const {t} = useTranslation(["integrations"]);
    const stores = useStore();
    const [integrations, setIntegrations] = useState([]);
    const [integrationsLoading, setIntegrationsLoading] = useState(true);
    const [integrationsFields, setIntegrationsFields] = useState([]);
    const [integrationFieldsLoading, setIntegrationFieldsLoading] = useState(true);
    const [selectedIntegration, setSelectedIntegration] = useState();

    const [createIntegrationModalVisibility, setCreateIntegrationModalVisibility] = useState(false);

    const [updateIntegrationModalVisibility, setUpdateIntegrationModalVisibility] = useState(false);

    const loadIntegrations = async () => {
        setIntegrationsLoading(true);
        const integrationsResponse = await stores.companyStore.listSubcompanyMealCardIntegrations({
            subCompanyId: subcompanyId
        });
        setIntegrations(integrationsResponse.data);
        setIntegrationsLoading(false);
    };

    const loadIntegrationsFields = async () => {
        setIntegrationFieldsLoading(true);
        const integrationsFieldsResponse = await stores.companyStore.listSubcompanyMealCardIntegrationsFields();
        setIntegrationsFields(integrationsFieldsResponse.data);
        setIntegrationFieldsLoading(false);
    };

    const handleAddIntegration = () => {
        setCreateIntegrationModalVisibility(true);
    };

    const closeModals = async (needRefresh = false) => {
        setCreateIntegrationModalVisibility(false);
        setUpdateIntegrationModalVisibility(false);
        setSelectedIntegration(undefined);
        if (needRefresh) {
            await loadIntegrations();
        }
    };

    useEffect(() => {
        loadIntegrations();
        loadIntegrationsFields();
    }, []);

    const showAddIntegrationButton = () => {
        const existIntegrationTypes = integrations.map(integration => integration.mealCard);
        const integrationTypes = integrationsFields.map(integration => integration.mealCard);
        if (integrationsLoading || integrationFieldsLoading) return false;

        return integrationTypes.some(
            integrationType => !existIntegrationTypes.includes(integrationType)
        );
    };

    const handleSelectIntegration = integration => {
        setSelectedIntegration(integration);
        setUpdateIntegrationModalVisibility(true);
    };

    return (
        <>
            <Col {...props}>
                <Row
                    type="flex"
                    justify="space-between"
                    style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        marginTop: "30px",
                        marginBottom: "20px"
                    }}
                >
                    {t("integrations.others.mealCardIntegrations", {ns: "integrations"})}
                    <Row type="flex" justify="end" gutter={4}>
                        <Tooltip title={t("integrations.actions.refresh", {ns: "integrations"})}>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                    onClick={loadIntegrations}
                                    loading={integrationsLoading && integrationFieldsLoading}
                                />
                            </Col>
                        </Tooltip>
                        <Tooltip
                            placement="topRight"
                            title={
                                showAddIntegrationButton()
                                    ? t("integrations.actions.addMealCard", {ns: "integrations"})
                                    : t("integrations.others.noMealCardFound", {ns: "integrations"})
                            }
                        >
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddIntegration}
                                    loading={integrationsLoading && integrationFieldsLoading}
                                    disabled={!showAddIntegrationButton()}
                                />
                            </Col>
                        </Tooltip>
                    </Row>
                </Row>
                <Row>
                    <Col span={24}>
                        <Table
                            columns={[
                                {
                                    title: t("integrations.commonFields.mealCard", {
                                        ns: "integrations"
                                    }),
                                    dataIndex: "mealCard",
                                    render: platform =>
                                        t(`integrations.mealCards.${platform}.name`, {
                                            ns: "integrations"
                                        })
                                },
                                {
                                    title: t("integrations.commonFields.isValid", {
                                        ns: "integrations"
                                    }),
                                    dataIndex: "isValid",
                                    key: "isValid",
                                    render: isValid =>
                                        isValid ? (
                                            <b style={{color: "#7E9E34"}}>
                                                {t("integrations.commonFields.verified", {
                                                    ns: "integrations"
                                                })}
                                            </b>
                                        ) : (
                                            <b style={{color: "#C42F20"}}>
                                                {t("integrations.commonFields.notVerified", {
                                                    ns: "integrations"
                                                })}
                                            </b>
                                        )
                                },
                                {
                                    title: t("integrations.commonFields.isActive", {
                                        ns: "integrations"
                                    }),
                                    dataIndex: "isActive",
                                    key: "isActive",
                                    render: isActive =>
                                        isActive
                                            ? t("other.active", {ns: "common"})
                                            : t("other.passive", {ns: "common"})
                                },
                                {
                                    title: "",
                                    dataIndex: "",
                                    key: "",
                                    render: (field, integration) => (
                                        <Tooltip title={t("actions.edit", {ns: "common"})}>
                                            <Button
                                                icon={<EditOutlined />}
                                                onClick={() => handleSelectIntegration(integration)}
                                            />
                                        </Tooltip>
                                    )
                                }
                            ]}
                            dataSource={integrations}
                            loading={integrationsLoading}
                            pagination={false}
                            rowKey={record => record.mealCard}
                            locale={{
                                emptyText: t("integrations.others.noMealCardIntegrationFound", {
                                    ns: "integrations"
                                })
                            }}
                            expandable={{
                                expandedRowRender: record => (
                                    <Descriptions bordered size="small">
                                        <Descriptions.Item
                                            label={t(`integrations.commonFields.mealCard`, {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {t(`integrations.mealCards.${record.mealCard}.name`, {
                                                ns: "integrations"
                                            })}
                                        </Descriptions.Item>
                                        {Object.entries(record)
                                            .filter(
                                                entry =>
                                                    ![
                                                        "isValid",
                                                        "isActive",
                                                        "mealCard",
                                                        "auth"
                                                    ].includes(entry[0])
                                            )
                                            .map(entry => (
                                                <Descriptions.Item
                                                    label={t(
                                                        `integrations.mealCards.${record.mealCard}.fields.${entry[0]}`,
                                                        {
                                                            ns: "integrations"
                                                        }
                                                    )}
                                                    span={3}
                                                    key={`expandedItemField${record?.mealCard}${entry[0]}`}
                                                    style={{width: "250px"}}
                                                >
                                                    <HideableText
                                                        value={entry?.[1].toString()}
                                                        defaultHidden={true}
                                                    />
                                                </Descriptions.Item>
                                            ))}
                                        <Descriptions.Item
                                            label={t("integrations.commonFields.isActive", {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {record.isActive
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("integrations.commonFields.isValid", {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {record.isValid ? (
                                                <b style={{color: "#7E9E34"}}>
                                                    {t("integrations.commonFields.verified", {
                                                        ns: "integrations"
                                                    })}
                                                </b>
                                            ) : (
                                                <b style={{color: "#C42F20"}}>
                                                    {t("integrations.commonFields.notVerified", {
                                                        ns: "integrations"
                                                    })}
                                                </b>
                                            )}
                                        </Descriptions.Item>
                                        {record?.auth && (
                                            <Descriptions.Item
                                                label={t("integrations.commonFields.auth", {
                                                    ns: "integrations"
                                                })}
                                                span={3}
                                            >
                                                <Popover
                                                    content={
                                                        <JSONInput
                                                            id="mealCardAuthJSON"
                                                            placeholder={record.auth}
                                                            theme="dark_mitsuketa_tribute"
                                                            width="auto"
                                                            height="auto"
                                                            viewOnly={true}
                                                            confirmGood={false}
                                                        />
                                                    }
                                                >
                                                    <InfoCircleOutlined
                                                        style={{paddingLeft: "5px"}}
                                                    />
                                                </Popover>
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                ),
                                rowExpandable: () => true
                            }}
                        />
                    </Col>
                </Row>
            </Col>
            <MealCardIntegrationCreateModal
                existIntegrationTypes={integrations.map(integration => integration.type)}
                subcompanyId={subcompanyId}
                integrationsFields={integrationsFields}
                visibility={createIntegrationModalVisibility}
                onCancel={closeModals}
            />
            <MealCardIntegrationUpdateModal
                subcompanyId={subcompanyId}
                selectedIntegration={selectedIntegration}
                integrationsFields={integrationsFields}
                visibility={updateIntegrationModalVisibility}
                onCancel={closeModals}
            />
        </>
    );
};
